import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSettings, applyBranding } from '../context/SettingsContext';
import { enablePushNotifications, disablePushNotifications } from '../hooks/usePushNotifications';
import { useT } from '../i18n';
import { useLang } from '../context/LangContext';

const SCANLINE_STYLE = `
@keyframes scan { 0% { top: 0% } 100% { top: 100% } }
@keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }
.scanline-bar {
  width: 100%; height: 2px;
  background: linear-gradient(to right, transparent, var(--brand-primary), transparent);
  position: absolute; top: 0; pointer-events: none;
  animation: scan 5s linear infinite; opacity: 0.07;
}
.settings-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 24px;
}
.settings-col-8  { grid-column: span 12; }
.settings-col-4  { grid-column: span 12; }
.settings-col-6  { grid-column: span 12; }
.settings-col-12 { grid-column: span 12; }
@media (min-width: 1024px) {
  .settings-col-8  { grid-column: span 8; }
  .settings-col-4  { grid-column: span 4; }
  .settings-col-6  { grid-column: span 6; }
}
.preset-swatch {
  width: 36px; height: 36px; cursor: pointer; border: 2px solid transparent;
  transition: border-color 0.15s, transform 0.15s;
  flex-shrink: 0;
}
.preset-swatch:hover { transform: scale(1.12); }
.preset-swatch.active { border-color: var(--on-surface) !important; }
`;

const L = {
  mono:    "'JetBrains Mono', monospace",
  display: "var(--font-display)",
};

const C = {
  surface: 'rgba(28,32,33,0.75)',
  high:    'rgba(39,43,44,0.6)',
  border:  'var(--outline-variant)',
  text:    'var(--on-surface)',
  muted:   'var(--outline)',
};

// Team color presets — primary + secondary pairs
const PRESETS = [
  { label: 'CYBER CYAN',   primary: 'var(--brand-primary)', secondary: '#feb700' },
  { label: 'RACE RED',     primary: '#ff3d3d', secondary: '#ffb700' },
  { label: 'VOLT GREEN',   primary: '#72eb00', secondary: 'var(--brand-primary)' },
  { label: 'BLAZE ORANGE', primary: '#ff6b00', secondary: '#ffdb00' },
  { label: 'PLASMA PINK',  primary: '#ff4dc4', secondary: '#c3f5ff' },
  { label: 'ULTRA VIOLET', primary: '#8b5cf6', secondary: 'var(--brand-primary)' },
  { label: 'COBALT BLUE',  primary: '#2563eb', secondary: '#72eb00' },
  { label: 'ARCTIC WHITE', primary: '#e0e3e4', secondary: 'var(--brand-primary)' },
];

function Label({ children, style }) {
  return (
    <span style={{ fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--outline)', ...style }}>
      {children}
    </span>
  );
}

function SectionCard({ children, style, scanline, className }) {
  return (
    <div
      className={className}
      style={{
        background: C.surface,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${C.border}`,
        position: 'relative', overflow: 'hidden',
        ...style,
      }}
    >
      {scanline && <div className="scanline-bar" />}
      {children}
    </div>
  );
}

function ColorRow({ label, value, onChange, onReset, resetValue }) {
  return (
    <div>
      <div style={{ fontFamily: L.mono, fontSize: 8, letterSpacing: '0.12em', color: 'var(--outline)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: C.high, border: `1px solid ${C.border}` }}>
        <div style={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
          <div style={{ width: 32, height: 32, background: value, border: '1px solid rgba(255,255,255,0.1)' }} />
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
          />
        </div>
        <span style={{ fontFamily: L.mono, fontSize: 13, fontWeight: 700, color: value, letterSpacing: '0.06em', flex: 1 }}>
          {value.toUpperCase()}
        </span>
        {value !== resetValue && (
          <button
            onClick={() => onChange(resetValue)}
            style={{ background: 'none', border: `1px solid ${C.border}`, color: 'var(--outline)', fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px', cursor: 'pointer' }}
          >RESET</button>
        )}
      </div>
    </div>
  );
}

function PublicPageButton({ slug }) {
  const [copied, setCopied] = React.useState(false);
  const url = `${window.location.origin}/t/${slug}`;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.12em' }}>ÖFFENTLICHE SEITE</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', letterSpacing: '0.04em' }}>
          /t/<strong style={{ color: 'var(--primary)' }}>{slug}</strong>
        </span>
        <button
          onClick={handleCopy}
          title="URL kopieren"
          style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', padding: '5px 10px',
            background: copied ? 'rgba(187,255,140,0.1)' : 'rgba(var(--brand-primary-rgb),0.08)',
            border: `1px solid ${copied ? 'rgba(187,255,140,0.4)' : 'rgba(var(--brand-primary-rgb),0.3)'}`,
            color: copied ? 'var(--tertiary)' : 'var(--primary)',
            cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}
        >
          {copied ? '✓ KOPIERT' : '⎘ KOPIEREN'}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title="Seite öffnen"
          style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', padding: '5px 10px',
            background: 'rgba(var(--brand-primary-rgb),0.08)',
            border: '1px solid rgba(var(--brand-primary-rgb),0.3)',
            color: 'var(--primary)', textDecoration: 'none', whiteSpace: 'nowrap',
          }}
        >
          ↗ ÖFFNEN
        </a>
      </div>
    </div>
  );
}

function Settings({ token, userInfo, role, isCoAdmin, onLogout, theme = 'dark', onToggleTheme }) {
  const isAdmin = role === 'admin';
  const canManageTeam = isAdmin || isCoAdmin;
  const t = useT();
  const { lang, setLang } = useLang();
  const fileRef = useRef();

  const {
    teamName:       ctxTeamName,
    colorPrimary:   ctxPrimary,
    colorSecondary: ctxSecondary,
    logoBase64:     ctxLogo,
    saveSettings,
  } = useSettings();

  /* ── Local edit state (mirrors context until saved) ── */
  const [teamName,   setTeamName]   = useState('');
  const [primary,    setPrimary]    = useState('var(--brand-primary)');
  const [secondary,  setSecondary]  = useState('#feb700');
  const [logoBase64, setLogo]       = useState('');
  const [logoPreview, setLogoPreview] = useState('');

  /* Sync from context once loaded */
  useEffect(() => {
    setTeamName(ctxTeamName || '');
    setPrimary(ctxPrimary   || 'var(--brand-primary)');
    setSecondary(ctxSecondary || '#feb700');
    setLogo(ctxLogo         || '');
    setLogoPreview(ctxLogo  || '');
  }, [ctxTeamName, ctxPrimary, ctxSecondary, ctxLogo]);

  /* ── Save state ── */
  const [saveMsg, setSaveMsg] = useState(null);
  const [saving,  setSaving]  = useState(false);

  /* ── Push notifications ── */
  const [pushPermission, setPushPermission] = useState('default');
  const [pushEnabled,    setPushEnabled]    = useState(false);
  const [pushLoading,    setPushLoading]    = useState(false);
  const [pushMsg,        setPushMsg]        = useState(null);

  useEffect(() => {
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
      const stored = localStorage.getItem('pushNotificationsEnabled');
      setPushEnabled(Notification.permission === 'granted' && stored !== 'false');
    }
  }, []);

  /* ── rad-net sync ── */
  const [radnetLoading,  setRadnetLoading]  = useState(false);
  const [radnetResults,  setRadnetResults]  = useState(null);

  /* ── public slug (fetched fresh so it works without re-login) ── */
  const [publicSlug, setPublicSlug] = useState(userInfo?.public_slug || null);
  useEffect(() => {
    if (!token) return;
    axios.get('/api/settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data.public_slug) setPublicSlug(r.data.public_slug); })
      .catch(() => {});
  }, [token]); // eslint-disable-line

  /* ── invite links ── */
  const [inviteLinks,    setInviteLinks]    = useState([]);
  const [newLabel,       setNewLabel]       = useState('');
  const [newExpiry,      setNewExpiry]      = useState(7);
  const [newMaxUses,     setNewMaxUses]     = useState('');
  const [creatingLink,   setCreatingLink]   = useState(false);
  const [linkMsg,        setLinkMsg]        = useState(null);
  const [copiedToken,    setCopiedToken]    = useState(null);

  /* ── owner transfer + team delete ── */
  const [teamDrivers,     setTeamDrivers]    = useState([]);
  const [transferTarget,  setTransferTarget] = useState(null);
  const [transferConfirm, setTransferConfirm]= useState(false);
  const [transferring,    setTransferring]   = useState(false);
  const [transferMsg,     setTransferMsg]    = useState(null);
  const [showDeleteModal, setShowDeleteModal]= useState(false);
  const [deletePassword,  setDeletePassword] = useState('');
  const [deleteNameInput, setDeleteNameInput]= useState('');
  const [deleting,        setDeleting]       = useState(false);
  const [deleteMsg,       setDeleteMsg]      = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    const h = { Authorization: `Bearer ${token}` };
    axios.get('/api/admin/invite-links', { headers: h }).then(r => setInviteLinks(r.data)).catch(() => {});
    axios.get('/api/drivers', { headers: h }).then(r => setTeamDrivers(r.data)).catch(() => {});
  }, [isAdmin, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRadnetSync = async () => {
    setRadnetLoading(true);
    setRadnetResults(null);
    try {
      const res = await axios.post('/api/admin/radnet-sync', {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000, // 2 min — fetching all pages can take a while
      });
      setRadnetResults(res.data);
    } catch (err) {
      setRadnetResults({ error: err.response?.data?.error || err.message });
    } finally {
      setRadnetLoading(false);
    }
  };

  /* Live-preview: apply to DOM as user drags sliders */
  const handlePrimaryChange = (val) => {
    setPrimary(val);
    applyBranding(val, secondary);
  };
  const handleSecondaryChange = (val) => {
    setSecondary(val);
    applyBranding(primary, val);
  };
  const applyPreset = (preset) => {
    setPrimary(preset.primary);
    setSecondary(preset.secondary);
    applyBranding(preset.primary, preset.secondary);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setLogo(ev.target.result); setLogoPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaveMsg(null); setSaving(true);
    try {
      await saveSettings({ teamName, colorPrimary: primary, colorSecondary: secondary, logoBase64 });
      setSaveMsg({ type: 'success', text: 'GESPEICHERT ✓' });
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.error || 'FEHLER BEIM SPEICHERN' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 4000);
    }
  };

  const handlePushToggle = async () => {
    setPushLoading(true); setPushMsg(null);
    try {
      if (pushEnabled) {
        await disablePushNotifications(token);
        setPushEnabled(false);
        setPushMsg({ type: 'info', text: 'Push-Benachrichtigungen deaktiviert.' });
      } else {
        if (Notification.permission === 'denied') throw new Error('DENIED');
        await enablePushNotifications(token);
        setPushPermission('granted'); setPushEnabled(true);
        setPushMsg({ type: 'success', text: 'Push-Benachrichtigungen aktiviert!' });
      }
    } catch (err) {
      if (err.message === 'DENIED') { setPushPermission('denied'); setPushMsg({ type: 'blocked', text: null }); }
      else if (err.message === 'DISMISSED') setPushMsg({ type: 'error', text: 'Berechtigung nicht erteilt.' });
      else setPushMsg({ type: 'error', text: err.message });
    } finally { setPushLoading(false); }
  };

  /* ── Invite-Link Handler ── */
  const handleCreateLink = async () => {
    setCreatingLink(true); setLinkMsg(null);
    try {
      const r = await axios.post('/api/admin/invite-links', {
        label: newLabel.trim() || null,
        expiresInDays: Number(newExpiry),
        maxUses: newMaxUses ? Number(newMaxUses) : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInviteLinks(prev => [r.data, ...prev]);
      setNewLabel(''); setNewMaxUses('');
      setLinkMsg({ type: 'success', text: 'Link erstellt ✓' });
      setTimeout(() => setLinkMsg(null), 4000);
    } catch (err) {
      setLinkMsg({ type: 'error', text: err.response?.data?.error || 'Fehler beim Erstellen.' });
    } finally { setCreatingLink(false); }
  };
  const handleRevokeLink = async (id) => {
    try {
      await axios.delete(`/api/admin/invite-links/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setInviteLinks(prev => prev.map(l => l.id === id ? { ...l, revoked: true } : l));
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler');
    }
  };
  const copyInviteLink = (tok) => {
    const url = `${window.location.origin}/join/${tok}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedToken(tok);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  /* ── Owner Transfer + Delete Handler ── */
  const handleTransferRequest = async () => {
    if (!transferTarget) return;
    setTransferring(true);
    try {
      await axios.post('/api/owner/transfer-request',
        { to_driver_account_id: transferTarget.driver_account_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransferMsg({ type: 'success', text: `Anfrage an ${transferTarget.name} gesendet. Der Fahrer wird per Push benachrichtigt.` });
      setTransferConfirm(false); setTransferTarget(null);
    } catch (err) {
      setTransferMsg({ type: 'error', text: err.response?.data?.error || 'Fehler' });
    } finally { setTransferring(false); }
  };
  const handleDeleteTeam = async () => {
    if (!deletePassword) { setDeleteMsg({ type: 'error', text: 'Passwort eingeben.' }); return; }
    if (deleteNameInput.trim().toUpperCase() !== (teamName || '').trim().toUpperCase()) {
      setDeleteMsg({ type: 'error', text: 'Teamname stimmt nicht überein.' }); return;
    }
    setDeleting(true); setDeleteMsg(null);
    try {
      await axios.delete('/api/owner/team', {
        data: { password: deletePassword },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onLogout) onLogout();
    } catch (err) {
      setDeleteMsg({ type: 'error', text: err.response?.data?.error || 'Fehler' });
      setDeleting(false);
    }
  };

  const initials = userInfo?.name
    ? userInfo.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : 'CR';

  const activePreset = PRESETS.find(p => p.primary.toLowerCase() === primary.toLowerCase() && p.secondary.toLowerCase() === secondary.toLowerCase());

  return (
    <div style={{ fontFamily: L.display, color: 'var(--on-surface)', minHeight: '100%' }}>
      <style>{SCANLINE_STYLE}</style>

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 6 }}>KONTO</div>
        <h1 style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 900, fontSize: 'clamp(1.6rem,4vw,2.5rem)', margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase', color: 'var(--on-surface)' }}>
          Settings
        </h1>
      </div>

      <div className="settings-grid">

        {/* ══ SPRACHE / LANGUAGE ══ */}
        <SectionCard className="settings-col-6">
          <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(195,245,255,0.08)` }}>
            <div style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{t('settings_section_lang')}</div>
            <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--on-surface-variant)', marginTop: 4 }}>{t('settings_lang_desc')}</div>
          </div>
          <div style={{ padding: '1.5rem 24px', display: 'flex', gap: 8 }}>
            {[{ code: 'de', label: 'DE — Deutsch' }, { code: 'en', label: 'EN — English' }].map(({ code, label }) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                style={{
                  flex: 1, padding: '10px 0',
                  fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
                  background: lang === code ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent',
                  border: `1px solid ${lang === code ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.4)'}`,
                  color: lang === code ? 'var(--primary)' : 'var(--outline)',
                  cursor: 'pointer', transition: 'all 0.15s', textTransform: 'uppercase',
                }}
              >{label}</button>
            ))}
          </div>
        </SectionCard>

        {/* ══ PUSH NOTIFICATIONS ══ */}
        {'Notification' in window && (
          <SectionCard className="settings-col-6">
            <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(195,245,255,0.08)` }}>
              <div style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{t('settings_section_push')}</div>
              <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--on-surface-variant)', marginTop: 4 }}>{t('settings_push_desc')}</div>
            </div>
            <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: pushPermission === 'granted' && pushEnabled ? 'var(--tertiary)'
                      : pushPermission === 'denied' ? 'var(--error)' : 'var(--outline-variant)',
                    boxShadow: pushPermission === 'granted' && pushEnabled ? '0 0 8px var(--tertiary)' : 'none',
                  }} />
                  <span style={{ fontFamily: L.mono, fontSize: 10, letterSpacing: '0.1em', color: 'var(--outline)' }}>
                    {pushPermission === 'denied' ? 'BLOCKIERT' : pushPermission === 'granted' && pushEnabled ? 'AKTIV' : 'INAKTIV'}
                  </span>
                </div>
                {pushPermission !== 'denied' && (
                  <button
                    onClick={handlePushToggle}
                    disabled={pushLoading}
                    style={{
                      position: 'relative', width: 52, height: 28, borderRadius: 14,
                      background: pushEnabled ? 'var(--tertiary-container)' : C.high,
                      border: `1px solid ${pushEnabled ? 'var(--tertiary-container)' : C.border}`,
                      cursor: pushLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s', outline: 'none', opacity: pushLoading ? 0.6 : 1, flexShrink: 0,
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 4, left: pushEnabled ? 27 : 4,
                      width: 18, height: 18, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                    }} />
                  </button>
                )}
              </div>

              {pushMsg && pushMsg.type !== 'blocked' && (
                <div style={{
                  padding: '10px 14px',
                  background: pushMsg.type === 'success' ? 'rgba(187,255,140,0.06)' : pushMsg.type === 'info' ? 'rgba(59,73,75,0.2)' : 'rgba(255,180,171,0.08)',
                  border: `1px solid ${pushMsg.type === 'success' ? 'rgba(187,255,140,0.25)' : pushMsg.type === 'info' ? 'rgba(59,73,75,0.4)' : 'rgba(255,180,171,0.3)'}`,
                  fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  color: pushMsg.type === 'success' ? 'var(--tertiary)' : pushMsg.type === 'info' ? 'var(--outline)' : 'var(--error)',
                }}>
                  {pushMsg.text}
                </div>
              )}

              {(pushPermission === 'denied' || (pushMsg && pushMsg.type === 'blocked')) && (
                <div style={{ padding: '14px 16px', background: 'rgba(255,180,171,0.06)', border: '1px solid rgba(255,180,171,0.2)' }}>
                  <div style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, color: 'var(--error)', letterSpacing: '0.12em', marginBottom: 10 }}>BERECHTIGUNG BLOCKIERT</div>
                  <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--on-surface-variant)', lineHeight: 1.7 }}>
                    Du hast Push-Benachrichtigungen blockiert. So entsperrst du sie:<br /><br />
                    <strong style={{ color: 'var(--on-surface)' }}>Safari (iOS):</strong><br />
                    Einstellungen → Safari → Erweitert → Website-Daten<br /><br />
                    <strong style={{ color: 'var(--on-surface)' }}>Chrome (Android/Desktop):</strong><br />
                    Adressleiste → 🔒 → Website-Einstellungen → Benachrichtigungen → Zulassen
                  </div>
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* ══ TEAM SETTINGS Subheader ══ */}
        {canManageTeam && (
          <div className="settings-col-12" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, paddingTop: 8 }}>
            <div>
              <div style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 4 }}>ADMIN</div>
              <h2 style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 800, fontSize: 'clamp(1.2rem,3vw,1.8rem)', margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
                Team Branding
              </h2>
            </div>
            {publicSlug && (
              <PublicPageButton slug={publicSlug} />
            )}
          </div>
        )}

        {/* ══ TEAM IDENTITY — col 8 ══ */}
        {canManageTeam && (
          <SectionCard scanline className="settings-col-8">
            <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(195,245,255,0.08)`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Team Identity</span>
            </div>

            <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: 32 }}>

              {/* ── Team Name ── */}
              <div>
                <Label style={{ display: 'block', marginBottom: 8 }}>Team Name</Label>
                <input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value.toUpperCase())}
                  placeholder="Z.B. STURMVOGEL RT"
                  maxLength={32}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: C.high, border: `1px solid ${C.border}`,
                    color: 'var(--primary)', fontFamily: L.display, fontStyle: 'italic',
                    fontSize: 20, fontWeight: 900, letterSpacing: '-0.01em', textTransform: 'uppercase',
                    padding: '12px 16px', outline: 'none',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; e.target.style.boxShadow = 'inset 0 0 12px rgba(var(--primary-rgb),0.08)'; }}
                  onBlur={(e)  => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* ── Color Presets ── */}
              <div>
                <Label style={{ display: 'block', marginBottom: 12 }}>Farb-Presets</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {PRESETS.map((preset) => {
                    const isActive = preset.primary.toLowerCase() === primary.toLowerCase();
                    return (
                      <button
                        key={preset.label}
                        onClick={() => applyPreset(preset)}
                        title={preset.label}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 0,
                          border: `2px solid ${isActive ? 'var(--on-surface)' : 'transparent'}`,
                          background: 'none', padding: 0, cursor: 'pointer',
                          transition: 'border-color 0.15s',
                        }}
                      >
                        <div style={{ width: 28, height: 28, background: preset.primary }} />
                        <div style={{ width: 28, height: 28, background: preset.secondary }} />
                      </button>
                    );
                  })}
                </div>
                {activePreset && (
                  <div style={{ fontFamily: L.mono, fontSize: 9, color: 'var(--outline)', letterSpacing: '0.1em', marginTop: 8 }}>
                    {activePreset.label}
                  </div>
                )}
              </div>

              {/* ── Colors ── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                <div>
                  <ColorRow
                    label="PRIMÄRFARBE — CTAs, aktive Elemente, Countdown"
                    value={primary}
                    onChange={handlePrimaryChange}
                    resetValue="var(--brand-primary)"
                  />
                </div>
                <div>
                  <ColorRow
                    label="SEKUNDÄRFARBE — Akzente, Badges, Highlights"
                    value={secondary}
                    onChange={handleSecondaryChange}
                    resetValue="#feb700"
                  />
                </div>
              </div>

              {/* Gradient preview bar */}
              <div style={{ height: 3, background: `linear-gradient(to right, ${primary}, ${secondary})` }} />

              {/* ── Logo Upload ── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24, alignItems: 'start' }}>
                <div>
                  <Label style={{ display: 'block', marginBottom: 12 }}>Team Logo</Label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      border: `2px dashed ${logoPreview ? 'rgba(195,245,255,0.3)' : C.border}`,
                      cursor: 'pointer', padding: '1.5rem', minHeight: 140, position: 'relative',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = logoPreview ? 'rgba(195,245,255,0.3)' : C.border}
                  >
                    <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                    {logoPreview ? (
                      <>
                        <img src={logoPreview} alt="Logo" style={{ maxWidth: 100, maxHeight: 100, objectFit: 'contain', marginBottom: 10 }} />
                        <Label>KLICKEN ZUM ERSETZEN</Label>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 28, color: 'var(--outline)', marginBottom: 10 }}>⬆</span>
                        <div style={{ fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--outline)', marginBottom: 4 }}>LOGO HOCHLADEN</div>
                        <div style={{ fontFamily: L.mono, fontSize: 9, color: 'rgba(132,147,150,0.5)' }}>SVG, PNG (Max 5MB)</div>
                      </>
                    )}
                  </div>
                  {logoPreview && (
                    <button
                      onClick={() => { setLogo(''); setLogoPreview(''); }}
                      style={{ marginTop: 8, background: 'none', border: `1px solid rgba(255,180,171,0.3)`, color: 'var(--error)', fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '5px 12px', cursor: 'pointer', width: '100%' }}
                    >
                      ✕ LOGO ENTFERNEN
                    </button>
                  )}
                </div>

                {/* What the logo affects */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Label style={{ display: 'block', marginBottom: 4 }}>Das Logo erscheint</Label>
                  {['In der Sidebar neben dem Teamnamen', 'Im Dashboard Hero-Bereich', 'Im Browser-Tab (Favicon)', 'In Push-Benachrichtigungen'].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 4, height: 4, background: 'var(--primary)', flexShrink: 0 }} />
                      <span style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--on-surface-variant)' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Save Footer ── */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid rgba(59,73,75,0.35)`, display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary"
                style={{ padding: '10px 28px', opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'SPEICHERT...' : 'SPEICHERN'}
              </button>
              {saveMsg && (
                <span style={{ fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: saveMsg.type === 'success' ? 'var(--tertiary)' : 'var(--error)' }}>
                  {saveMsg.text}
                </span>
              )}
            </div>
          </SectionCard>
        )}

        {/* ══ LIVE PREVIEW — col 4 ══ */}
        {canManageTeam && (
          <SectionCard className="settings-col-4" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--tertiary-container)', boxShadow: '0 0 8px var(--tertiary-container)', animation: 'pulse-dot 2s infinite' }} />
                <Label style={{ color: 'var(--tertiary)' }}>Live Preview</Label>
              </div>
            </div>

            <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Sidebar mock */}
              <div style={{ background: 'rgba(11,15,16,0.95)', border: `1px solid ${C.border}`, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, background: `${primary}22`, border: `1px solid ${primary}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontFamily: L.display, fontStyle: 'italic', fontSize: 13, fontWeight: 900, color: primary }}>{initials}</span>
                  }
                </div>
                <div style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 900, fontSize: 15, letterSpacing: '-0.01em', textTransform: 'uppercase', color: primary, textShadow: `0 0 10px ${primary}66` }}>
                  {teamName || 'TEAM NAME'}
                </div>
              </div>

              {/* Color tokens */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label style={{ marginBottom: 4 }}>Farbwirkung</Label>
                {[
                  { label: 'PRIMARY — Buttons / Aktiv', color: primary },
                  { label: 'SECONDARY — Badges / Warns', color: secondary },
                  { label: 'TERTIARY — Erfolg', color: 'var(--tertiary)' },
                  { label: 'ERROR — Abgemeldet', color: 'var(--error)' },
                ].map(({ label, color }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 20, height: 20, background: color, flexShrink: 0, border: '1px solid rgba(255,255,255,0.06)' }} />
                    <span style={{ fontFamily: L.mono, fontSize: 9, color: 'var(--outline)', letterSpacing: '0.06em' }}>{label}</span>
                  </div>
                ))}
              </div>

              {/* Button mock */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Label style={{ marginBottom: 4 }}>Button-Vorschau</Label>
                <div style={{ padding: '10px 18px', background: primary, fontFamily: L.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: '#001820', textAlign: 'center', boxShadow: `0 0 16px ${primary}44` }}>
                  ANMELDEN
                </div>
                <div style={{ padding: '10px 18px', border: `1px solid ${primary}`, fontFamily: L.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: primary, textAlign: 'center' }}>
                  DETAILS
                </div>
              </div>

              {/* Gradient strip */}
              <div style={{ height: 3, background: `linear-gradient(to right, ${primary}, ${secondary})` }} />

              {/* Status chips */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { label: 'ANGEMELDET', bg: `${primary}20`, border: `${primary}66`, color: primary },
                  { label: 'ABGEMELDET', bg: 'rgba(147,0,10,0.15)', border: 'rgba(255,180,171,0.4)', color: 'var(--error)' },
                  { label: 'PFLICHT',    bg: `${secondary}30`, border: `${secondary}66`, color: secondary },
                ].map(({ label, bg, border, color }) => (
                  <div key={label} style={{ padding: '3px 8px', background: bg, border: `1px solid ${border}`, fontFamily: L.mono, fontSize: 8, fontWeight: 700, color, letterSpacing: '0.08em' }}>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        )}

        {/* ══ RAD-NET SYNC ══ */}
        {isAdmin && (
          <SectionCard className="settings-col-12">
            <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(195,245,255,0.08)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Punkte Sync & Rangliste</div>
                <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--outline)', marginTop: 4 }}>
                  Punkte &amp; Rennklassen automatisch aus der BDR-Rangliste übernehmen
                </div>
              </div>
              <button
                onClick={handleRadnetSync}
                disabled={radnetLoading}
                className="btn btn-primary"
                style={{ padding: '10px 24px', opacity: radnetLoading ? 0.6 : 1, cursor: radnetLoading ? 'not-allowed' : 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {radnetLoading ? (
                  <>
                    <span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid rgba(0,0,0,0.3)', borderTopColor: '#001820', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    SYNC LÄUFT...
                  </>
                ) : '⟳ JETZT SYNCHRONISIEREN'}
              </button>
            </div>

            <div style={{ padding: '1.5rem 24px' }}>
              {/* Info text */}
              {!radnetResults && !radnetLoading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    'Fahrer werden über Name + Verein abgeglichen',
                    'Verein im Fahrerprofil hinterlegen für eindeutiges Matching',
                    'Unterstützte Kategorien: Elite, Amateure, U23, U19, U17, Master 1–4, Elite Frauen, U23 Frauen, U19/U17 Juniorinnen',
                    'Punkte werden direkt im Fahrerprofil aktualisiert',
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ width: 4, height: 4, background: 'var(--primary)', flexShrink: 0, marginTop: 6 }} />
                      <span style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--on-surface-variant)', lineHeight: 1.6 }}>{item}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Error state */}
              {radnetResults?.error && (
                <div style={{ padding: '12px 16px', background: 'rgba(255,180,171,0.08)', border: '1px solid rgba(255,180,171,0.3)', fontFamily: L.mono, fontSize: 11, fontWeight: 700, color: 'var(--error)', letterSpacing: '0.06em' }}>
                  ✕ FEHLER: {radnetResults.error}
                </div>
              )}

              {/* Results */}
              {radnetResults && !radnetResults.error && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Summary */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Gematcht', value: radnetResults.summary?.matched ?? 0, color: 'var(--tertiary)', bg: 'rgba(187,255,140,0.08)', border: 'rgba(187,255,140,0.25)' },
                      { label: 'Nicht gefunden', value: radnetResults.summary?.not_found ?? 0, color: 'var(--outline)', bg: 'rgba(59,73,75,0.2)', border: 'rgba(59,73,75,0.4)' },
                      { label: 'Mehrdeutig', value: radnetResults.summary?.ambiguous ?? 0, color: '#ff9a1f', bg: 'rgba(255,154,31,0.08)', border: 'rgba(255,154,31,0.3)' },
                    ].map(({ label, value, color, bg, border }) => (
                      <div key={label} style={{ padding: '10px 20px', background: bg, border: `1px solid ${border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <span style={{ fontFamily: L.mono, fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</span>
                        <span style={{ fontFamily: L.mono, fontSize: 9, color: 'var(--outline)', letterSpacing: '0.1em' }}>{label.toUpperCase()}</span>
                      </div>
                    ))}
                    <div style={{ padding: '10px 20px', background: 'rgba(28,32,33,0.5)', border: '1px solid var(--outline-variant)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                      <span style={{ fontFamily: L.mono, fontSize: 9, color: 'var(--outline)', letterSpacing: '0.08em' }}>SAISON {radnetResults.saison}</span>
                      <span style={{ fontFamily: L.mono, fontSize: 9, color: 'var(--outline)', letterSpacing: '0.08em' }}>
                        {new Date(radnetResults.syncedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                      </span>
                    </div>
                  </div>

                  {/* Per-driver results */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {radnetResults.results?.map((r, i) => {
                      const statusColor = r.status === 'matched' ? 'var(--tertiary)' : r.status === 'ambiguous' ? '#ff9a1f' : 'var(--outline)';
                      const statusBg    = r.status === 'matched' ? 'rgba(187,255,140,0.06)' : r.status === 'ambiguous' ? 'rgba(255,154,31,0.06)' : 'rgba(26,28,32,0.4)';
                      const statusLabel = { matched: 'OK', not_found: '—', ambiguous: '!', skipped: 'SKIP' }[r.status] || r.status;
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 12px', background: statusBg, borderLeft: `2px solid ${statusColor}` }}>
                          <span style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, color: statusColor, letterSpacing: '0.08em', minWidth: 32, flexShrink: 0, marginTop: 2 }}>{statusLabel}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--on-surface)', fontWeight: 600 }}>{r.driver}</span>
                            {r.status === 'matched' && (
                              <span style={{ fontFamily: L.mono, fontSize: 10, color: 'var(--outline)', marginLeft: 10 }}>
                                {r.points} Pkt · {r.verein} · Rang {r.rang}
                              </span>
                            )}
                            {r.status === 'ambiguous' && (
                              <div style={{ fontFamily: L.mono, fontSize: 10, color: 'var(--outline)', marginTop: 3 }}>
                                {r.matches?.join(' / ')}
                                {r.hint && <span style={{ color: '#ff9a1f', marginLeft: 6 }}>→ {r.hint}</span>}
                              </div>
                            )}
                            {r.status === 'skipped' && (
                              <span style={{ fontFamily: L.mono, fontSize: 10, color: 'var(--outline)', marginLeft: 10 }}>{r.reason}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </SectionCard>
        )}

        {/* ══ EINLADUNGSLINKS ══ */}
        {isAdmin && (
          <SectionCard className="settings-col-12">
            <div style={{ padding: '20px 24px', borderBottom: `1px solid rgba(195,245,255,0.08)` }}>
              <div style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Einladungslinks</div>
              <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--outline)', marginTop: 4 }}>
                Fahrer per Link einladen — ohne E-Mail. Der Link ist zeitlich begrenzt und optional auf eine maximale Nutzerzahl beschränkt.
              </div>
            </div>
            <div style={{ padding: '1.5rem 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

              {/* Create form */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
                <div>
                  <Label style={{ display: 'block', marginBottom: 6 }}>Label (optional)</Label>
                  <input
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    placeholder="z.B. Trainingsgruppe"
                    maxLength={60}
                    style={{ width: '100%', boxSizing: 'border-box', background: C.high, border: `1px solid ${C.border}`, color: 'var(--on-surface)', fontFamily: "'Geist', sans-serif", fontSize: 13, padding: '9px 12px', outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                    onBlur={e => e.target.style.borderColor = C.border}
                  />
                </div>
                <div>
                  <Label style={{ display: 'block', marginBottom: 6 }}>Gültig für</Label>
                  <select
                    value={newExpiry}
                    onChange={e => setNewExpiry(e.target.value)}
                    style={{ width: '100%', background: C.high, border: `1px solid ${C.border}`, color: 'var(--on-surface)', fontFamily: L.mono, fontSize: 11, padding: '9px 12px', outline: 'none', cursor: 'pointer' }}
                  >
                    {[3, 7, 14, 30, 90].map(d => <option key={d} value={d}>{d} Tage</option>)}
                  </select>
                </div>
                <div>
                  <Label style={{ display: 'block', marginBottom: 6 }}>Max. Nutzungen</Label>
                  <input
                    type="number"
                    value={newMaxUses}
                    onChange={e => setNewMaxUses(e.target.value)}
                    placeholder="∞ unbegrenzt"
                    min="1" max="10000"
                    style={{ width: '100%', boxSizing: 'border-box', background: C.high, border: `1px solid ${C.border}`, color: 'var(--on-surface)', fontFamily: "'Geist', sans-serif", fontSize: 13, padding: '9px 12px', outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                    onBlur={e => e.target.style.borderColor = C.border}
                  />
                </div>
                <button
                  onClick={handleCreateLink}
                  disabled={creatingLink}
                  className="btn btn-primary"
                  style={{ padding: '10px 20px', opacity: creatingLink ? 0.6 : 1, cursor: creatingLink ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  {creatingLink ? 'ERSTELLE...' : '+ LINK ERSTELLEN'}
                </button>
              </div>

              {linkMsg && (
                <div style={{ padding: '10px 14px', background: linkMsg.type === 'success' ? 'rgba(187,255,140,0.06)' : 'rgba(255,180,171,0.08)', border: `1px solid ${linkMsg.type === 'success' ? 'rgba(187,255,140,0.25)' : 'rgba(255,180,171,0.3)'}`, fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: linkMsg.type === 'success' ? 'var(--tertiary)' : 'var(--error)' }}>
                  {linkMsg.text}
                </div>
              )}

              {/* Links list */}
              {inviteLinks.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Label style={{ display: 'block', marginBottom: 4 }}>Aktive Links</Label>
                  {inviteLinks.map(link => {
                    const url = `${window.location.origin}/join/${link.token}`;
                    const expired = new Date(link.expires_at) < new Date();
                    const exhausted = link.max_uses !== null && link.use_count >= link.max_uses;
                    const inactive = link.revoked || expired || exhausted;
                    return (
                      <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: inactive ? 'rgba(26,28,32,0.3)' : C.high, border: `1px solid ${inactive ? 'rgba(59,73,75,0.25)' : C.border}`, opacity: inactive ? 0.55 : 1, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 160 }}>
                          {link.label && (
                            <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, fontWeight: 600, color: 'var(--on-surface)', marginBottom: 3 }}>{link.label}</div>
                          )}
                          <div style={{ fontFamily: L.mono, fontSize: 9, color: 'var(--outline)', letterSpacing: '0.08em', wordBreak: 'break-all' }}>{url}</div>
                          <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: L.mono, fontSize: 8, color: inactive ? 'var(--error)' : 'var(--outline)', letterSpacing: '0.06em' }}>
                              {link.revoked ? '✕ WIDERRUFEN' : expired ? '✕ ABGELAUFEN' : exhausted ? '✕ LIMIT ERREICHT' : `LÄUFT AB ${new Date(link.expires_at).toLocaleDateString('de-DE')}`}
                            </span>
                            <span style={{ fontFamily: L.mono, fontSize: 8, color: 'var(--outline)', letterSpacing: '0.06em' }}>
                              {link.use_count}{link.max_uses !== null ? `/${link.max_uses}` : ''} NUTZUNGEN
                            </span>
                          </div>
                        </div>
                        {/* QR mini-preview */}
                        {!inactive && (
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&bgcolor=1C2021&color=00DAF3&data=${encodeURIComponent(url)}`}
                            alt="QR"
                            style={{ width: 60, height: 60, flexShrink: 0, border: '1px solid var(--outline-variant)' }}
                          />
                        )}
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                          {!inactive && (
                            <button
                              onClick={() => copyInviteLink(link.token)}
                              style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '6px 12px', background: copiedToken === link.token ? 'rgba(187,255,140,0.1)' : 'rgba(var(--brand-primary-rgb),0.08)', border: `1px solid ${copiedToken === link.token ? 'rgba(187,255,140,0.4)' : 'rgba(var(--brand-primary-rgb),0.3)'}`, color: copiedToken === link.token ? 'var(--tertiary)' : 'var(--primary)', cursor: 'pointer' }}
                            >
                              {copiedToken === link.token ? '✓ KOPIERT' : '⎘ LINK'}
                            </button>
                          )}
                          {!link.revoked && (
                            <button
                              onClick={() => handleRevokeLink(link.id)}
                              style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '6px 12px', background: 'transparent', border: '1px solid rgba(255,180,171,0.3)', color: 'var(--error)', cursor: 'pointer' }}
                            >
                              WIDERRUFEN
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* ══ TEAM MANAGEMENT (Owner-Transfer + Löschen) ══ */}
        {isAdmin && (
          <>
            {/* Subheader */}
            <div className="settings-col-12" style={{ paddingTop: 8 }}>
              <div style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--error)', marginBottom: 4 }}>GEFAHRENZONE</div>
              <h2 style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 800, fontSize: 'clamp(1.2rem,3vw,1.8rem)', margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase', color: 'var(--on-surface)' }}>
                Team Ownership
              </h2>
            </div>

            {/* Owner Transfer */}
            <SectionCard className="settings-col-6" style={{ borderColor: 'rgba(255,154,31,0.25)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,154,31,0.15)' }}>
                <div style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#ff9a1f' }}>Teambesitz übertragen</div>
                <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--outline)', marginTop: 4 }}>
                  Der ausgewählte Fahrer erhält eine Anfrage. Du verlierst nach Annahme den Admin-Zugang.
                </div>
              </div>
              <div style={{ padding: '1.5rem 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <Label style={{ display: 'block', marginBottom: 8 }}>Fahrer auswählen</Label>
                  <select
                    value={transferTarget ? transferTarget.driver_account_id : ''}
                    onChange={e => {
                      const id = Number(e.target.value);
                      const d = teamDrivers.find(dr => dr.driver_account_id === id);
                      setTransferTarget(d || null);
                      setTransferConfirm(false);
                      setTransferMsg(null);
                    }}
                    style={{ width: '100%', background: C.high, border: `1px solid ${C.border}`, color: 'var(--on-surface)', fontFamily: "'Geist', sans-serif", fontSize: 13, padding: '10px 12px', outline: 'none', cursor: 'pointer' }}
                  >
                    <option value="">— Fahrer wählen —</option>
                    {teamDrivers.filter(d => d.driver_account_id).map(d => (
                      <option key={d.driver_account_id} value={d.driver_account_id}>{d.name}</option>
                    ))}
                  </select>
                </div>

                {transferTarget && !transferConfirm && (
                  <button
                    onClick={() => setTransferConfirm(true)}
                    style={{ padding: '10px 20px', fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(255,154,31,0.1)', border: '1px solid rgba(255,154,31,0.4)', color: '#ff9a1f', cursor: 'pointer' }}
                  >
                    OWNERSHIP AN {transferTarget.name?.toUpperCase()} ÜBERTRAGEN
                  </button>
                )}

                {transferTarget && transferConfirm && (
                  <div style={{ padding: '16px', background: 'rgba(255,140,0,0.06)', border: '1px solid rgba(255,160,50,0.4)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ fontFamily: L.mono, fontSize: 10, fontWeight: 700, color: '#ff9a1f', letterSpacing: '0.1em' }}>⚠ BITTE BESTÄTIGEN</div>
                    <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--on-surface-variant)', lineHeight: 1.6 }}>
                      Du überträgst das Team-Ownership an <strong style={{ color: 'var(--on-surface)' }}>{transferTarget.name}</strong>. Nach Annahme verlierst du den Admin-Zugang zu diesem Team.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleTransferRequest} disabled={transferring}
                        style={{ flex: 1, padding: '10px', fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: '#ff9a1f', color: '#1a0800', border: 'none', cursor: transferring ? 'not-allowed' : 'pointer', opacity: transferring ? 0.6 : 1 }}>
                        {transferring ? 'SENDE...' : 'JA, ANFRAGE SENDEN'}
                      </button>
                      <button onClick={() => { setTransferConfirm(false); setTransferTarget(null); }}
                        style={{ padding: '10px 16px', fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: 'transparent', border: `1px solid ${C.border}`, color: 'var(--outline)', cursor: 'pointer' }}>
                        ABBRECHEN
                      </button>
                    </div>
                  </div>
                )}

                {transferMsg && (
                  <div style={{ padding: '10px 14px', background: transferMsg.type === 'success' ? 'rgba(187,255,140,0.06)' : 'rgba(255,180,171,0.08)', border: `1px solid ${transferMsg.type === 'success' ? 'rgba(187,255,140,0.25)' : 'rgba(255,180,171,0.3)'}`, fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: transferMsg.type === 'success' ? 'var(--tertiary)' : 'var(--error)' }}>
                    {transferMsg.text}
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Team Delete */}
            <SectionCard className="settings-col-6" style={{ borderColor: 'rgba(220,50,50,0.3)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(220,50,50,0.15)' }}>
                <div style={{ fontFamily: L.display, fontStyle: 'italic', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--error)' }}>Team löschen</div>
                <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--outline)', marginTop: 4 }}>
                  Löscht das Team, alle Fahrer, Events und Daten unwiderruflich.
                </div>
              </div>
              <div style={{ padding: '1.5rem 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!showDeleteModal ? (
                  <button
                    onClick={() => { setShowDeleteModal(true); setDeleteMsg(null); }}
                    style={{ padding: '11px 20px', fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,50,50,0.4)', color: 'var(--error)', cursor: 'pointer' }}
                  >
                    ✕ TEAM LÖSCHEN
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ padding: '12px 14px', background: 'rgba(180,20,20,0.08)', border: '1px solid rgba(220,50,50,0.3)', fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--error)', lineHeight: 1.6 }}>
                      Diese Aktion ist <strong>unwiderruflich</strong>. Alle Daten werden gelöscht.
                    </div>
                    <div>
                      <Label style={{ display: 'block', marginBottom: 6 }}>Teamnamen zur Bestätigung eingeben</Label>
                      <input
                        value={deleteNameInput}
                        onChange={e => setDeleteNameInput(e.target.value)}
                        placeholder={teamName || 'TEAMNAME'}
                        style={{ width: '100%', boxSizing: 'border-box', background: C.high, border: '1px solid rgba(220,50,50,0.4)', color: 'var(--on-surface)', fontFamily: L.mono, fontSize: 12, padding: '9px 12px', outline: 'none', textTransform: 'uppercase' }}
                      />
                    </div>
                    <div>
                      <Label style={{ display: 'block', marginBottom: 6 }}>Admin-Passwort</Label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={e => setDeletePassword(e.target.value)}
                        placeholder="••••••••••"
                        style={{ width: '100%', boxSizing: 'border-box', background: C.high, border: '1px solid rgba(220,50,50,0.4)', color: 'var(--on-surface)', fontFamily: L.mono, fontSize: 12, padding: '9px 12px', outline: 'none' }}
                      />
                    </div>
                    {deleteMsg && (
                      <div style={{ padding: '10px 14px', background: 'rgba(255,180,171,0.08)', border: '1px solid rgba(255,180,171,0.3)', fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--error)' }}>
                        {deleteMsg.text}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleDeleteTeam} disabled={deleting}
                        style={{ flex: 1, padding: '11px', fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: '#dc2626', color: '#fff', border: 'none', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1 }}>
                        {deleting ? 'LÖSCHE...' : '✕ ENDGÜLTIG LÖSCHEN'}
                      </button>
                      <button onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteNameInput(''); setDeleteMsg(null); }}
                        style={{ padding: '11px 16px', fontFamily: L.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: 'transparent', border: `1px solid ${C.border}`, color: 'var(--outline)', cursor: 'pointer' }}>
                        ABBRECHEN
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>
          </>
        )}

      </div>

      {/* ── Mobile: Logout ── */}
      {onLogout && (
        <div style={{ marginTop: 32 }}>
          <div style={{ fontFamily: L.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 12 }}>{t('settings_section_account').toUpperCase()}</div>
          <button
            onClick={onLogout}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', background: 'rgba(26,28,32,0.6)', border: `1px solid ${C.border}`, color: 'var(--on-surface-variant)', fontFamily: L.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', width: '100%' }}
          >
            <span style={{ fontSize: 16 }}>⏻</span> {t('nav_logout').toUpperCase()}
          </button>
        </div>
      )}
    </div>
  );
}

export default Settings;
